import _ from 'lodash';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { selectDialogState } from 'app/dialog/features/dialog.selectors';
import { DialogService } from 'app/dialog/services/dialog.service';
import { ChampionsImportService } from '../../services/champions-import.service';
import { ChampionsPresetOptionSchema } from 'app/campaign/structure/champions';
import { loadP2PCampaignList, peerToPeerReport } from 'app/manage-p2p/features/p2p.actions';
import { ToastService } from 'app/toast/services/toast.service';
import * as ExcelJS from 'exceljs/dist/exceljs';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-champions-import',
  templateUrl: './champions-import.component.html',
  styleUrls: ['./champions-import.component.scss']
})
export class ChampionsImportComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public dialog$: Observable<any> = this.store.pipe(select(selectDialogState));
  
  public schema: any = ChampionsPresetOptionSchema;
  public readmore: boolean = false;
  
  public import: FormControl = new FormControl(null, []);
  public sendNotification: FormControl = new FormControl(false);

  public champions: FormArray = new FormArray([], []);
  
  public confirm: boolean = true;
  public confirmDisabled: boolean = true;
  public closeButton: boolean = false;
  
  public campaignId: string = '';
  public teams: [];
  public loadCampaignList: boolean = false;

  constructor(
    private store: Store,
    private dialogService: DialogService,
    private championsImportService: ChampionsImportService,
    private toastService: ToastService,
    private sharedService: SharedService,
  ) { }

  ngOnInit(): void {
    this.dialog$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((dialog) => {
      const { teams, campaignId, loadCampaignList } = dialog.initialState;
      this.campaignId = campaignId;
      this.teams = teams || [];
      this.loadCampaignList = loadCampaignList;
    })
  }

  public downloadExcelTemplate() {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Fundraisers');
    const teams = this.teams;

    const columns = [
      { header: 'Name', headerStyle: { bold: true }, width: 20 },
      { header: 'Email', headerStyle: { bold: true }, width: 20 },
      { header: 'Goal', headerStyle: { bold: true }, width: 15 }
    ];
    
    if(teams.length === 0) {
      sheet.columns = columns;

      workbook.xlsx.writeBuffer().then((buffer) => {
        return this.saveAsExcelFile(buffer);
      });
    }

    const teamColumn = { header: 'Team Name', headerStyle: { bold: true }, width: 20 };
    columns.push(teamColumn);

    sheet.columns = columns;

    const dropdownOptions = teams.map((team: any) => team.name).join(',');

    //populate dropdown only for the first 50 rows excluding header
    const rowCount = 51;
    for (let i = 2; i <= rowCount; i++) { 
      const cell = sheet.getCell(`D${i}`);
      cell.dataValidation = {
        type: "list",
        formulae: [`"${dropdownOptions}"`],
        showErrorMessage: true,
        errorTitle: 'Invalid Team',
        error: 'Please select a Team Name from the dropdown.',
      };
    }

    workbook.xlsx.writeBuffer().then((buffer) => {
      this.saveAsExcelFile(buffer);
    });
  }

  private saveAsExcelFile(buffer){
    const blob: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = 'sample_fundraisers_import';
    downloadLink.click();
  }

  onChangeData(champions) {
    this.champions = champions;

    if (this.champions.length < 1) {
      return;
    } 
    this.confirm = true;

    if (this.champions.status === 'VALID') {
      this.confirmDisabled = false;
    } else {
      this.confirmDisabled = true;
    }
  }

  onConfirm(dialog) {
    this.confirm = false;
    this.closeButton = true;
  }

  onClose(dialog) {
    this.championsImportService.addFundraiserList(this.campaignId, this.champions.value, this.sendNotification.value).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((response: any) => {
      let toastType = 'success';
      let toastMessage = 'Imported Successfully';

      if(response.failedCount > 0) {
        toastType = 'danger';
        toastMessage = 'Error Importing Champions';
      }
      this.toastService.show(toastType, toastMessage);

      this.store.dispatch({
        type: peerToPeerReport.type,
        payload: this.campaignId
      });

      this.close(dialog, true);

      if(this.loadCampaignList) {
        this.store.dispatch({
          type: loadP2PCampaignList.type,
          payload: this.sharedService.token
        });
      }
    });
  }

  close(dialog, value) {
    this.dialogService.closeDialogSubject.next(value);
  }

  onToggleReadMore() {
    this.readmore = !this.readmore;
  }

  ngOnDestroy(): void {
    this.dialogService.closeDialogSubject.next(null);
  }
}
